import React, { useState, FC, useEffect } from 'react';
import { MONTHS, WEEKDAYS_LONG, WEEKDAYS_SHORT, FIRST_DAY_OF_WEEK } from '../CalenderTranslations';
import DayPicker, { DateUtils } from 'react-day-picker';
import { CalendarChange, IMokkiPackage, SelectionType } from '../CalendarPage';
import { DateHelpers } from '../DateHelpers';
import {
	Heading,
	Text,
	Divider,
	Grid,
	Box,
	Button,
	Select,
	Alert,
	AlertIcon,
	AlertDescription,
	Badge
} from '@chakra-ui/react';
import 'react-day-picker/lib/style.css';
import Icons from '../../../Components/Icons/icons';
import { HasUnsavedChangesConfirmation } from '../../../Components/HasUnsavedChangesConfirmation';
import i18n from '../../../Translation/Translation';
import { useTranslation } from 'react-i18next';
import { Helpers } from '../../../Utils/Helpers';
import { AxiosResponse } from 'axios';
import { LegendExplanations } from './Legend';

export interface MokkiCalendarProps {
	product: IMokkiPackage;
	year: number;
	setYear: (x: string) => void;
	save: (x: CalendarChange[]) => Promise<AxiosResponse<boolean>>;
	refreshCalendar: () => void;
}

interface ICalenderDay {
	isSelected: boolean;
	isLocked: boolean;
	id: number;
	dateString: string;
	date: Date;
	originalSelectedState: boolean;
	hasChanged: boolean;
}

const createInitialCalender = (days: IMokkiPackage['days']): ICalenderDay[] =>
	Helpers.cloneWithDates(days).map((d) => ({
		...d,
		hasChanged: false,
		originalSelectedState: d.isSelected
	}));

export const MokkiCalendar: FC<MokkiCalendarProps> = (props) => {
	const { product, year, setYear, save, refreshCalendar } = props;
	const [calendarDays, setCalendarDays] = useState<ICalenderDay[]>(
		createInitialCalender(product.days)
	);
	const [showSuccess, setShowSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { t } = useTranslation('calendar');

	useEffect(() => {
		// re-initialise calender when we get new product from props
		setCalendarDays(createInitialCalender(product.days));
	}, [product]);

	const handleSave = () => {
		setIsSubmitting(true);

		const changedDates = calendarDays
			.filter((item) => item.hasChanged)
			.map((item) => ({ id: item.id, isSelected: item.isSelected }));

		save(changedDates).then(
			(success) => {
				setShowSuccess(true);
				setIsSubmitting(false);
				refreshCalendar();
			},
			(fail) => {
				setIsSubmitting(false);
				console.error(fail);
			}
		);
	};

	const getCalendarDaysClone = () => Helpers.cloneWithDates(calendarDays);

	const getSelectedDays = () => calendarDays.filter((d) => d.isSelected).map((d) => d.date);

	const getDisabledDays = () => calendarDays.filter((d) => d.isLocked).map((d) => d.date);

	const handleDayClick = (p: Date, modifiers = {} as any) => {
		// do nothing if date was disabled
		if (modifiers.disabled) return;

		// get selected
		const selected = modifiers.selected ? true : false;

		setShowSuccess(false);

		// handle selection based on selectiontype
		product.selectionType === SelectionType.continuous
			? doMonthSelection(p, selected)
			: doDaySelection(p, selected);
	};

	const doMonthSelection = (d: Date, isSelected: boolean) => {
		// console.log('doing sliding selection');
		const dayString = DateHelpers.dateToISOString(d);
		const dayIndex = calendarDays.findIndex((day) => day.dateString === dayString);
		const dayExists = dayIndex > -1;
		const newSelectionState = !isSelected;

		// gatekeeper
		if (!dayExists) return;

		// create immutable new calendar
		let calendar = getCalendarDaysClone();
		let daysToModify = [] as ICalenderDay[];
		let daysToHold = [] as ICalenderDay[];

		// update calender
		calendar.forEach((item) => {
			// modify only upcoming and selected day
			if ((item.date > d || DateUtils.isSameDay(d, item.date)) && !item.isLocked) {
				// update state and push to altered
				daysToModify.push({
					...item,
					isSelected: newSelectionState,
					hasChanged: item.originalSelectedState !== newSelectionState
				});
			} else {
				daysToHold.push(item);
			}
		});

		// combine lists
		const newCalendar = [...daysToModify, ...daysToHold];

		setCalendarDays(newCalendar);
	};

	const doDaySelection = (d: Date, isSelected: boolean) => {
		const dayString = DateHelpers.dateToISOString(d);
		const dayIndex = calendarDays.findIndex((day) => day.dateString === dayString);
		const dayExists = dayIndex > -1;
		const newSelectionState = !isSelected;

		// gatekeeper
		if (!dayExists) return;

		// create immutable daylist selected list
		let calendar = getCalendarDaysClone();

		// toggle state for clicked day
		calendar[dayIndex].isSelected = newSelectionState;
		calendar[dayIndex].hasChanged = calendar[dayIndex].originalSelectedState !== newSelectionState;

		// bail if too many selections
		if (newSelectionState === true && product && dayCount + 1 > product.maxSelected) return;

		setCalendarDays(calendar);
	};

	const localeLang = i18n.language;
	const calenderType =
		product.selectionType === SelectionType.continuous ? 'daypicker-selectype-month' : '';
	const availableYears = product.availableYears || [];
	const selectedDays = getSelectedDays();
	const disabledDays = getDisabledDays();
	const todayIsActive = currentDayWasSelected(selectedDays);
	const hasUnsavedChanges = calendarDays.some((item) => item.hasChanged);
	const dayCount = calendarDays.reduce(
		(previous, current) => previous + (current.isSelected ? 1 : 0),
		0
	);

	return (
		<>
			<Heading as="h3" fontSize="lg" d="flex" alignItems="center">
				{product.name}{' '}
				<Badge size="xxl" mr={2}>
					{year}
				</Badge>
			</Heading>

			<Text pt={2} fontSize="sm">
				{product.description}
			</Text>

			<Alert
				fontSize="sm"
				status={todayIsActive ? 'success' : 'info'}
				borderRadius="md"
				mt={2}
				px={2}
			>
				{todayIsActive
					? t('SERVICE_OPEN_TODAY_NOTIFICATION')
					: t('SERVICE_CLOSED_TODAY_NOTIFICATION')}
			</Alert>

			<Divider mt={4} mb={4} />

			<Grid templateColumns="16rem 1fr" gap="1rem">
				{/* Left Content */}
				<div>
					{product?.selectionType === SelectionType.day && (
						<Alert
							fontSize="sm"
							status={product && dayCount <= product.maxSelected ? 'info' : 'warning'}
							borderRadius="md"
							px={2}
						>
							<div>
								<Text>{`${t('SELECTABLE_LEFT')} ${product.maxSelected - dayCount}`}</Text>
								<Text fontSize="xs">{`(maksimi ${product.maxSelected})`}</Text>
							</div>
						</Alert>
					)}

					{availableYears.length > 0 && (
						<>
							<Text fontSize="sm" mt={4}>
								{'Valittavat vuodet'}
							</Text>
							<Select mt={2} defaultValue={year} onChange={(e) => setYear(e.target.value)}>
								{availableYears.map((year) => (
									<option key={year.toString()} value={year.toString()}>
										{year.toString()}
									</option>
								))}
							</Select>
						</>
					)}

					<Button
						onClick={handleSave}
						type="button"
						disabled={isSubmitting || !hasUnsavedChanges}
						leftIcon={Icons.save}
						mt={2}
						isLoading={isSubmitting}
					>
						{t('SAVE_BTN')}
					</Button>

					<Divider mt={4} mb={4} />

					<Text fontSize="sm" my={2}>
						{product.selfcareDescription || t('USAGE_GUIDE')}
					</Text>

					<Divider mt={4} mb={4} />

					<LegendExplanations />
				</div>

				{/* Right Content */}
				<Box borderLeft="1px solid" borderColor="gray.100" pl={4} className={calenderType}>
					{showSuccess && !hasUnsavedChanges && (
						<Alert fontSize="sm" borderRadius="md" status={'success'} px={2}>
							<AlertIcon />
							<AlertDescription>{t('CHANGES_APPLY_NOTIFICATION')}</AlertDescription>
						</Alert>
					)}

					{hasUnsavedChanges && (
						<Alert fontSize="sm" status="info" borderRadius="md" px={2}>
							<AlertIcon />
							<Text>{t('UNSAVED_WARNING')}</Text>
						</Alert>
					)}

					<HasUnsavedChangesConfirmation isDirty={hasUnsavedChanges} />

					<DayPicker
						canChangeMonth={false}
						month={new Date(year, 0)}
						numberOfMonths={12}
						selectedDays={selectedDays}
						renderDay={(day) => renderDay(day, selectedDays)}
						disabledDays={disabledDays}
						onDayClick={handleDayClick}
						//translations
						months={MONTHS[parseLocale(localeLang)]}
						weekdaysLong={WEEKDAYS_LONG[parseLocale(localeLang)]}
						weekdaysShort={WEEKDAYS_SHORT[parseLocale(localeLang)]}
						firstDayOfWeek={FIRST_DAY_OF_WEEK[parseLocale(localeLang)]}
					/>
				</Box>
			</Grid>
		</>
	);
};

const renderDay = (day: Date, dates: Date[]) => (
	<div title={includesDate(dates, day) ? 'Palvelu aktiivinen' : 'Palvelu passiivinen'}>
		{day.getDate()}
	</div>
);

const includesDate = (days: Date[], date: Date) =>
	days.some((day) => DateUtils.isSameDay(day, date));

const currentDayWasSelected = (selectedDays: Date[]): boolean => {
	const currentDay = DateHelpers.dateToISOString(new Date());
	const selected = selectedDays.map((day) => DateHelpers.dateToISOString(day));

	// selected.includes(currentDay) ? alert('Current day included') : alert('Not included');
	return selected.includes(currentDay);
};

const parseLocale = (language: string) => language.substring(0, 2);

// const initialState = {
// 	selectedDays: [] as Date[],
// 	alertType: 'DEFAULT' as 'UNSAVED' | 'DEFAULT' | 'SAVED',
// 	dayCount: 0,
// 	monthCount: 0,
// 	disabledDays: [] as Date[]
// };

// type State = typeof initialState;
// type Action =
// 	| { type: 'ADD_DAY'; payload: Date }
// 	| { type: 'REMOVE_DAY'; payload: number }
// 	| { type: 'ADD_MONTH'; payload: Date }
// 	| { type: 'REMOVE_MONTH'; payload: Date }
// 	| { type: 'SAVED' };

// const reducer = (state: State, action: Action) => {
// 	switch (action.type) {
// 		case 'ADD_DAY':
// 			return {
// 				...state,
// 				dayCount: state.dayCount + 1,
// 				alertType: 'UNSAVED',
// 				selectedDays: [...state.selectedDays, action.payload]
// 			};
// 		case 'REMOVE_DAY':
// 			return { ...state, dayCount: state.dayCount - 1, alertType: 'UNSAVED', selectedDays: [] };
// 		case 'ADD_MONTH':
// 			return { ...state, dayCount: state.monthCount + 1, alertType: 'UNSAVED', selectedDays: [] };
// 		case 'REMOVE_MONTH':
// 			return { ...state, dayCount: state.monthCount - 1, alertType: 'UNSAVED', selectedDays: [] };
// 		case 'SAVED':
// 			return { ...state, alertState: 'SAVED' };
// 		default:
// 			return { ...state };
// 	}
// };
